import { useEffect, useState } from "react";
import { api } from "../../api";
import { Btn, Card } from "../../components/ui";
import Icon from "../../components/Icon";
import { BRAND, BRAND_TINT, HERO_GRAD } from "../../theme";

/* ---------- helpers ---------- */

function toEmbedUrl(u) {
  let m = u.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  m = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  return u;
}

function extractIframeSrc(html) {
  const m = html.match(/<iframe[^>]*\ssrc\s*=\s*["']([^"']+)["']/i);
  return m ? m[1] : null;
}

/* ---------- video ---------- */

function VideoBox({ url }) {
  const u = (url || "").trim();
  const isFile = /\.(mp4|webm|ogg|mov)(\?|$)/i.test(u) || u.startsWith("/api/files/");
  const embedSrc = u.startsWith("<") ? extractIframeSrc(u) : null;
  const src = embedSrc || (u && !isFile && !u.startsWith("<") ? toEmbedUrl(u) : "");
  return (
    <div className="relative aspect-video w-full rounded-lg overflow-hidden bg-slate-900 flex items-center justify-center text-white/70">
      {isFile ? <video src={u} controls playsInline preload="metadata" className="absolute inset-0 w-full h-full" />
        : src ? <iframe src={src} title="lesson" className="absolute inset-0 w-full h-full" style={{ border: 0 }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen />
          : u.startsWith("<") ? <div className="absolute inset-0 w-full h-full [&_iframe]:!absolute [&_iframe]:!inset-0 [&_iframe]:!w-full [&_iframe]:!h-full [&_iframe]:!max-w-none [&_iframe]:!border-0" dangerouslySetInnerHTML={{ __html: u }} />
            : <div className="flex flex-col items-center gap-2"><Icon name="play" size={40} /><span className="text-sm">No video</span></div>}
    </div>
  );
}

/* ---------- file viewer (view-only, no download) ---------- */

function FileViewer({ url, name, onClose }) {
  const isPdf = /\.pdf$/i.test(name || "");
  const isPpt = /\.(pptx?|ppt)$/i.test(name || "");
  const isImage = /\.(png|jpe?g|gif|webp|svg)$/i.test(name || "");
  let src = url;
  if (isPdf || isPpt) src = url + "#toolbar=0&navpanes=0";
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex flex-col" onClick={onClose}>
      <div className="flex items-center justify-between px-4 py-2 bg-slate-900 text-white shrink-0">
        <span className="text-sm font-medium truncate">{name}</span>
        <button onClick={onClose} className="text-white/70 hover:text-white text-lg px-2">✕</button>
      </div>
      <div className="flex-1 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {isImage
          ? <div className="w-full h-full flex items-center justify-center p-4"><img src={url} alt={name} className="max-w-full max-h-full object-contain" /></div>
          : <iframe src={src} title={name} className="w-full h-full border-0" />}
      </div>
    </div>
  );
}

/* ---------- slide viewer (Google Slides embed) ---------- */

function SlideViewer({ slide, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex flex-col" onClick={onClose}>
      <div className="flex items-center justify-between px-4 py-2 bg-slate-900 text-white shrink-0">
        <span className="text-sm font-medium truncate">{slide.name || "Slides"}</span>
        <button onClick={onClose} className="text-white/70 hover:text-white text-lg px-2">✕</button>
      </div>
      <div className="flex-1 overflow-hidden p-4" onClick={(e) => e.stopPropagation()}>
        <iframe src={slide.url} title={slide.name} className="w-full h-full border-0 rounded-lg"
          sandbox="allow-scripts allow-same-origin allow-popups" allowFullScreen />
      </div>
    </div>
  );
}

/* ---------- lesson content ---------- */

function Lesson({ sub, onNext, hasNext, go }) {
  const [viewingFile, setViewingFile] = useState(null);
  const [viewingSlide, setViewingSlide] = useState(null);

  const materials = sub.materials || [];
  const slides = sub.slide_urls || [];

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-slate-900">{sub.title}</h2>
      {sub.video_url && <VideoBox url={sub.video_url} />}
      {sub.description && <p className="text-slate-600 text-sm leading-relaxed">{sub.description}</p>}

      {/* Materials — view only, no download */}
      {materials.length > 0 && (
        <div>
          <div className="font-semibold text-slate-800 mb-2 underline">Study material</div>
          <div className="space-y-2">
            {materials.map((mt, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                <span className="text-sm text-slate-700">{mt.name} <span className="text-[11px] uppercase tracking-wide text-slate-400 ml-1">{mt.type}</span></span>
                {mt.url
                  ? <button onClick={() => setViewingFile({ url: api.fileUrl(mt.url), name: mt.name })}
                      className="text-sm underline" style={{ color: BRAND }}>View</button>
                  : <span className="text-slate-300"><Icon name="eye" size={16} /></span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Slides (Google Slides embeds) */}
      {slides.length > 0 && (
        <div>
          <div className="font-semibold text-slate-800 mb-2 underline">Presentations</div>
          <div className="space-y-2">
            {slides.map((sl, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                <span className="text-sm text-slate-700"><Icon name="monitor" size={14} className="inline mr-2 text-slate-400" />{sl.name || `Slides ${i + 1}`}</span>
                <button onClick={() => setViewingSlide(sl)}
                  className="text-sm underline" style={{ color: BRAND }}>View</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MCQs — view only, no copy/select */}
      {(sub.questions || []).length > 0 && (() => {
        const sets = {};
        (sub.questions || []).forEach((q) => { const s = q.question_set || "A"; (sets[s] = sets[s] || []).push(q); });
        const setKeys = Object.keys(sets).sort();
        return (
          <div className="select-none" onCopy={(e) => e.preventDefault()} onCut={(e) => e.preventDefault()} onContextMenu={(e) => e.preventDefault()}
            style={{ WebkitUserSelect: "none", MozUserSelect: "none", msUserSelect: "none", userSelect: "none" }}>
            <div className="font-semibold text-slate-800 mb-2 underline">Assessment questions</div>
            <div className="text-xs text-slate-400 mb-3">Pass mark: {sub.pass_mark}%</div>
            {setKeys.map((setKey) => (
              <div key={setKey}>
                {setKeys.length > 1 && <div className="text-sm font-medium text-slate-600 mt-4 mb-2">Set {setKey}</div>}
                <div className="space-y-3">
                  {sets[setKey].map((q, qi) => (
                    <Card key={q.id} className="p-4">
                      <div className="font-medium text-slate-800 mb-2">{qi + 1}. {q.text}</div>
                      <div className="space-y-1">
                        {(q.options || []).map((o, oi) => (
                          <div key={oi} className={`flex items-center gap-2 text-sm rounded-lg px-3 py-1.5 ${oi === q.correct_index ? "bg-green-50 text-green-800 font-medium" : "text-slate-600"}`}>
                            <span className="w-5 h-5 rounded-full border flex items-center justify-center text-[11px] shrink-0"
                              style={oi === q.correct_index ? { borderColor: "#16a34a", backgroundColor: "#16a34a", color: "#fff" } : { borderColor: "#d6d3d1" }}>
                              {String.fromCharCode(65 + oi)}
                            </span>
                            <span>{o}</span>
                            {oi === q.correct_index && <Icon name="check" size={14} className="text-green-600 ml-auto shrink-0" />}
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {viewingFile && <FileViewer url={viewingFile.url} name={viewingFile.name} onClose={() => setViewingFile(null)} />}
      {viewingSlide && <SlideViewer slide={viewingSlide} onClose={() => setViewingSlide(null)} />}

      {hasNext && <Btn onClick={onNext}>Next lesson →</Btn>}
    </div>
  );
}

/* ---------- intro ---------- */

function Intro({ course }) {
  return (
    <div>
      <div className="rounded-xl h-44 flex items-end px-6 pb-5 text-white text-2xl font-bold mb-6 bg-cover bg-center relative overflow-hidden"
        style={course.thumbnail_url ? { backgroundImage: `url(${api.fileUrl(course.thumbnail_url)})` } : { backgroundImage: HERO_GRAD }}>
        {course.thumbnail_url && <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />}
        <span className="relative z-10">{course.course_name}</span>
      </div>
      {course.overview && (<div className="mb-4"><h3 className="text-lg font-semibold text-slate-900 mb-2">Course overview</h3><div className="rt text-sm text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: course.overview }} /></div>)}
      {course.certifications && (<div className="mb-4"><h3 className="text-lg font-semibold text-slate-900 mb-2">Certifications</h3><div className="rt text-sm text-slate-600" dangerouslySetInnerHTML={{ __html: course.certifications }} /></div>)}
      {course.general_instructions && (<div><h3 className="text-lg font-semibold text-slate-900 mb-2">Instructions</h3><div className="rt text-sm text-slate-600" dangerouslySetInnerHTML={{ __html: course.general_instructions }} /></div>)}
    </div>
  );
}

/* ---------- left menu ---------- */

function Menu({ course, view, setView }) {
  const activeSubId = view.type === "lesson" ? view.subId : null;
  const moduleOfActive = activeSubId
    ? course.modules.find((m) => (m.sub_modules || []).some((sm) => sm.id === activeSubId))
    : null;
  const [openMods, setOpenMods] = useState(
    () => new Set([moduleOfActive ? moduleOfActive.id : (course.modules[0] && course.modules[0].id)].filter(Boolean))
  );
  useEffect(() => {
    if (moduleOfActive) setOpenMods((s) => new Set(s).add(moduleOfActive.id));
  }, [moduleOfActive && moduleOfActive.id]);
  const toggleMod = (id) => setOpenMods((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const item = (label, active, onClick) => (
    <button key={label} onClick={onClick}
      className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm text-left mb-1 ${active ? "text-white" : "text-slate-700 hover:bg-slate-100"}`}
      style={active ? { backgroundColor: BRAND } : {}}>
      <span className="truncate">{label}</span>
    </button>
  );

  return (
    <Card className="p-4 w-64 shrink-0 h-fit max-h-[80vh] overflow-y-auto">
      <div className="text-sm font-semibold text-slate-500 mb-3">Course content</div>
      {item("Introduction", view.type === "intro", () => setView({ type: "intro" }))}
      {course.modules.map((m, i) => {
        const open = openMods.has(m.id);
        const subs = m.sub_modules || [];
        return (
          <div key={m.id} className="mt-1">
            <button onClick={() => toggleMod(m.id)}
              className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-slate-100 text-left">
              <Icon name={open ? "chevron-down" : "chevron-right"} size={14} className="text-slate-400 shrink-0" />
              <span className="flex-1 font-bold text-slate-800 text-[13px] truncate">Module {i + 1}: {m.title}</span>
            </button>
            {open && (
              <div className="ml-3 pl-2 border-l-2 border-slate-100 mt-1">
                {subs.map((sm) => item(sm.title || "Untitled lesson", view.type === "lesson" && view.subId === sm.id,
                  () => setView({ type: "lesson", subId: sm.id })))}
                {subs.length === 0 && <div className="text-[11px] text-slate-300 px-3 py-1">No lessons yet</div>}
              </div>
            )}
          </div>
        );
      })}
      {(course.final_questions || []).length > 0 && (
        <div className="mt-3 pt-2 border-t border-slate-100">
          {item("Final exam questions", view.type === "final", () => setView({ type: "final" }))}
        </div>
      )}
    </Card>
  );
}

/* ---------- final exam questions (view only) ---------- */

function FinalQuestions({ questions }) {
  return (
    <div className="select-none" onCopy={(e) => e.preventDefault()} onCut={(e) => e.preventDefault()} onContextMenu={(e) => e.preventDefault()}
      style={{ WebkitUserSelect: "none", MozUserSelect: "none", msUserSelect: "none", userSelect: "none" }}>
      <h2 className="text-xl font-bold text-slate-900 mb-4">Final exam questions</h2>
      <div className="space-y-3">
        {questions.map((q, qi) => (
          <Card key={q.id} className="p-4">
            <div className="font-medium text-slate-800 mb-2">{qi + 1}. {q.text}</div>
            <div className="space-y-1">
              {(q.options || []).map((o, oi) => (
                <div key={oi} className={`flex items-center gap-2 text-sm rounded-lg px-3 py-1.5 ${oi === q.correct_index ? "bg-green-50 text-green-800 font-medium" : "text-slate-600"}`}>
                  <span className="w-5 h-5 rounded-full border flex items-center justify-center text-[11px] shrink-0"
                    style={oi === q.correct_index ? { borderColor: "#16a34a", backgroundColor: "#16a34a", color: "#fff" } : { borderColor: "#d6d3d1" }}>
                    {String.fromCharCode(65 + oi)}
                  </span>
                  <span>{o}</span>
                  {oi === q.correct_index && <Icon name="check" size={14} className="text-green-600 ml-auto shrink-0" />}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------- main component ---------- */

export default function TrainerCourseView({ courseId, onBack, toast }) {
  const [course, setCourse] = useState(null);
  const [view, setView] = useState({ type: "intro" });

  useEffect(() => {
    api.trainerCourse(courseId).then(setCourse).catch((e) => toast && toast(e.message));
  }, [courseId]);

  if (!course) return <div className="text-slate-400 text-sm">Loading…</div>;

  const flatSubs = [];
  course.modules.forEach((m) => (m.sub_modules || []).forEach((sm) => flatSubs.push(sm)));
  const sub = view.subId ? flatSubs.find((sm) => sm.id === view.subId) : null;
  const nextSubOf = (id) => { const i = flatSubs.findIndex((sm) => sm.id === id); return i >= 0 ? flatSubs[i + 1] || null : null; };

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4">
        <Icon name="arrow-left" size={16} />Back to courses
      </button>
      <div className="flex gap-6 items-start">
        <Menu course={course} view={view} setView={setView} />
        <div className="flex-1 min-w-0">
          {view.type === "intro" && <Intro course={course} />}
          {view.type === "lesson" && sub && (() => {
            const next = nextSubOf(sub.id);
            return <Lesson sub={sub} hasNext={!!next} go={setView}
              onNext={() => setView(next ? { type: "lesson", subId: next.id } : { type: "intro" })} />;
          })()}
          {view.type === "final" && (course.final_questions || []).length > 0 && <FinalQuestions questions={course.final_questions} />}
        </div>
      </div>
    </div>
  );
}
